import { FC, Fragment, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Backup,
  HomeWork,
  MedicalServices,
  Badge,
  ArrowDropDown,
  InsertDriveFile,
  Folder,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  FormatAlignLeft,
  FormatAlignRight,
  VisibilityOff,
} from '@mui/icons-material';
import { t } from 'i18next';

type IconVariantTypes =
  | 'backup'
  | 'homeWork'
  | 'medicalServices'
  | 'badge'
  | 'report'
  | 'folder'
  | 'arrowUp'
  | 'arrowDown'
  | 'alignLeft'
  | 'alignRight'
  | 'hide'
  | 'none';

const iconVariants: Record<IconVariantTypes, React.ReactNode> = {
  backup: <Backup sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />,
  homeWork: <HomeWork sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />,
  medicalServices: (
    <MedicalServices sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  badge: <Badge sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />,
  report: (
    <InsertDriveFile sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  folder: <Folder sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />,
  arrowUp: (
    <ArrowUpward sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  arrowDown: (
    <ArrowDownward sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  alignLeft: (
    <FormatAlignLeft sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  alignRight: (
    <FormatAlignRight sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />
  ),
  hide: <VisibilityOff sx={{ height: '16px', color: 'rgba(0, 0, 0, 0.60)' }} />,
  none: <></>,
};

export interface DropdownMenuItem {
  label: string;
  onClick: () => void;
  iconName: keyof typeof iconVariants;
  borderTop?: boolean;
  disabled?: boolean;
  selected?: boolean;
}

interface DropdownProps {
  menuList: DropdownMenuItem[];
  actionMenu?: boolean;
  buttonComponent?: React.ReactNode;
  disabled?: boolean;
}

export const Dropdown: FC<DropdownProps> = ({
  menuList,
  actionMenu,
  buttonComponent,
  disabled,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const id = anchorEl ? 'simple-popover' : undefined;

  const dropdownComponent = buttonComponent || (
    <Button
      endIcon={<ArrowDropDown />}
      sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
      color='secondary'
      variant='contained'
    >
      {t('general.add')}
    </Button>
  );

  return (
    <>
      <Box
        sx={{
          bgcolor: anchorEl ? 'rgba(19, 63, 110, 0.3)' : 'unset',
        }}
        onClick={handleClick}
      >
        {actionMenu ? (
          <IconButton>
            <MoreVert
              color={disabled ? 'disabled' : 'primary'}
              sx={{ height: '24px' }}
            />
          </IconButton>
        ) : (
          dropdownComponent
        )}
      </Box>
      <Popover
        id={id}
        open={!!anchorEl}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            py: 1,
            minWidth: 180,
          }}
        >
          {menuList.map((menuItem, idx) => {
            return (
              <Fragment key={idx}>
                {menuItem.borderTop && (
                  <Divider sx={{ height: 1, width: '100%', my: 0.5 }} />
                )}
                <Button
                  sx={{
                    px: 2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    bgcolor: menuItem.selected
                      ? 'action.selected'
                      : 'transparent',
                    '&:hover': {
                      bgcolor: menuItem.selected
                        ? 'action.selected'
                        : 'action.hover',
                    },
                  }}
                  startIcon={iconVariants[menuItem.iconName]}
                  disabled={menuItem.disabled}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    menuItem.onClick();
                    handleClose(event);
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: menuItem.disabled
                        ? 'text.disabled'
                        : 'text.primary',
                    }}
                  >
                    {menuItem.label}
                  </Typography>
                </Button>
              </Fragment>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};
